import React, { useState } from "react";

import { Link } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useTypedResource } from "../i18n/languagehandler";

const Container = styled.nav`
  width: 100%;
  background: #ffffffcc;
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Brand = styled(Link)`
  font-weight: 700;
  font-size: 22px;
  color: var(--primary);
  text-decoration: none;
`;

const Menu = styled.ul<{ open: boolean }>`
  list-style: none;
  display: flex;
  gap: 20px;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    background: #fff;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding: 16px 20px;
    flex-direction: column;
    display: ${(p) => (p.open ? "flex" : "none")};
  }
`;

const Item = styled.li``;

const MenuLink = styled(Link)`
  text-decoration: none;
  color: var(--text);
  font-weight: 500;
  padding: 8px 10px;
  border-radius: 8px;
  &:hover {
    background: rgba(55, 90, 127, 0.08);
    color: var(--primary);
  }
`;

const Burger = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: inline-flex;
  }
  border: none;
  background: transparent;
  font-size: 22px;
`;

export const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const res = useTypedResource<{ [k: string]: string }>("menu");
  const { i18n } = useTranslation();

  const normalizedLang = (i18n.resolvedLanguage || i18n.language || "en").split(
    "-"
  )[0];

  const languageToCountry: Record<string, string> = {
    da: "dk",
    en: "gb",
    de: "de",
    bs: "ba",
  };

  const getFlagUrl = (lang: string) => {
    const cc = languageToCountry[lang] || lang;
    return `https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/${cc}.svg`;
  };

  return (
    <Container>
      <Inner>
        <Brand to="/">Aida Bikic</Brand>
        <Burger onClick={() => setOpen((s) => !s)} aria-label="menu">
          ☰
        </Burger>
        <Menu open={open} onClick={() => setOpen(false)}>
          <Item>
            <MenuLink to="/">{res.home}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/lectures">{res.lectures}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/courses">{res.courses}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/books">{res.books}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/podcast">{res.podcast}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/media">{res.media}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/about">{res.about}</MenuLink>
          </Item>
          <Item>
            <MenuLink to="/contact">{res.contact}</MenuLink>
          </Item>
        </Menu>
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setLangOpen((s) => !s)}
            aria-haspopup="listbox"
            aria-expanded={langOpen}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "6px 10px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            <img
              src={getFlagUrl(normalizedLang)}
              alt=""
              width={18}
              height={12}
              style={{ borderRadius: 2 }}
            />
            {normalizedLang.toUpperCase()}
          </button>
          {langOpen && (
            <ul
              role="listbox"
              style={{
                position: "absolute",
                right: 0,
                marginTop: 8,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                listStyle: "none",
                padding: 6,
                minWidth: 120,
                zIndex: 1001,
              }}
              onClick={() => setLangOpen(false)}
            >
              {(["da", "en", "de", "bs"] as const).map((lang) => (
                <li key={lang}>
                  <button
                    onClick={() => {
                      i18n.changeLanguage(lang);
                      localStorage.setItem("language", lang);
                      localStorage.setItem("i18nextLng", lang);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={getFlagUrl(lang)}
                      alt=""
                      width={18}
                      height={12}
                      style={{ borderRadius: 2 }}
                    />
                    {lang.toUpperCase()}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Inner>
    </Container>
  );
};
